import './styles/white.css';
import '../../../../style.css';

import type { TextAreaProps, TextFieldProps } from '@zinc/ui';

import { FormControl, TextArea, TextField, View } from '@zinc/ui';
import cx from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { cache } from 'tm3-core/cache';

import type { IFieldProps } from './IFieldProps';

export interface IProps extends IFieldProps {
  value?: string;
  ref?: React.ForwardedRef<any>;
  inputAttributes?: any;
  dataTestId?: string;
  forceLegacy?: boolean;
  icon?: React.ReactNode;
  endIcon?: React.ReactNode;
  removeLegacyPadding?: boolean;
}

export const Field = React.forwardRef((props: IProps, ref) => {
  return <NewUxField {...props} ref={ref} />;
});

type TRTextAreaProps = Required<TextAreaProps>;
type TRTextFieldProps = Required<TextFieldProps>;

type TOnFocus = TRTextAreaProps['onFocus'] & TRTextFieldProps['onFocus'];
type TOnBlur = TRTextAreaProps['onBlur'] & TRTextFieldProps['onBlur'];
type TOnChange = TRTextAreaProps['onChange'] & TRTextFieldProps['onChange'];

const NewUxField = React.memo(
  React.forwardRef((props: IProps, ref) => {
    const {
      className,
      dataTestId,
      dLabel,
      endAdornment,
      errorText,
      inputProps,
      inputComponent,
      inputAttributes,
      label,
      liveOnChange = true,
      maxLength,
      multiLine,
      placeholder,
      startAdornment,
      spreadToInputComponent,
      submitted,
      value,
      onBlur,
      onChange,
      onFocus,
      removeLegacyPadding = false,
      ...other
    } = props;

    const _className = useMemo(() => {
      return cx(
        className,
        errorText && !submitted ? 'border !border-orange-300' : 'border',
        errorText && submitted ? 'border !border-red-300' : 'border'
      );
    }, [className, errorText, submitted]);

    const computedLabel = useMemo(
      () => (dLabel ? cache.dynamicLabels.GetLabel(dLabel)?.text?.trim() || label || '' : label),
      [label, dLabel]
    );

    const [localValue, setLocalValue] = useState(value);
    const onChangeHandler: TOnChange = useCallback(
      (e) => {
        if (!liveOnChange) {
          setLocalValue(e?.value);
          return;
        }

        if (e?.event) {
          onChange?.(e.event);
          return;
        }

        // Zinc TextField can emit a value payload without a native event.
        if (Object.hasOwn(e ?? {}, 'value')) {
          const fallbackEvent = {
            target: { value: e.value },
            currentTarget: { value: e.value }
          };
          onChange?.(fallbackEvent);
        }
      },
      [onChange, liveOnChange]
    );

    const onBlurHandler: TOnBlur = useCallback(
      (e) => {
        if (!liveOnChange) {
          e.target.value = localValue ?? '';
          onChange?.(e);
        }
        onBlur?.(e);
      },
      [onChange, onBlur, localValue, liveOnChange]
    );

    const onFocusHandler: TOnFocus = useCallback(
      (e) => {
        // Default focus behavior; avoid caret jumping to 0
        const length = e.target.value?.length ?? 0;
        requestAnimationFrame(() => {
          e.target.setSelectionRange(length, length);
          if (onFocus) onFocus(e);
        });
      },
      [onFocus]
    );

    let content;
    if (multiLine) {
      content = (
        <TextArea
          endSlot={endAdornment}
          startSlot={startAdornment}
          onBlur={onBlurHandler}
          onFocus={onFocusHandler}
          {...other}
          className={_className}
          data-testid={dataTestId ?? label}
          hasError={submitted && !!errorText}
          inputAttributes={{
            ...spreadToInputComponent,
            ...inputAttributes,
            maxLength,
            'data-testid': dataTestId ?? label,
            style: { border: 'none', wordBreak: 'break-word' }
          }}
          inputComponent={inputComponent}
          inputProps={inputProps}
          placeholder={placeholder ?? (String.isString(computedLabel) ? computedLabel : undefined)}
          value={liveOnChange ? value : localValue}
          onChange={onChangeHandler}
        />
      );
    } else {
      content = (
        <TextField
          endSlot={endAdornment}
          startSlot={startAdornment}
          onBlur={onBlurHandler}
          onFocus={onFocusHandler}
          {...other}
          className={_className}
          data-testid={dataTestId ?? label}
          hasError={submitted && !!errorText}
          inputAttributes={{
            ref,
            ...spreadToInputComponent,
            ...inputAttributes,
            maxLength,
            autoComplete: 'off',
            'data-testid': dataTestId ?? label
          }}
          inputComponent={inputComponent}
          inputProps={inputProps}
          placeholder={placeholder ?? (String.isString(computedLabel) ? computedLabel : undefined)}
          value={liveOnChange ? value : localValue}
          onChange={onChangeHandler}
        />
      );
    }

    return (
      <View paddingBottom={removeLegacyPadding ? 0 : 2} className={className}>
        <FormControl hasError={submitted && !!errorText}>
          {computedLabel && <FormControl.Label>{computedLabel}</FormControl.Label>}
          {content}
          <FormControl.Error>{errorText}</FormControl.Error>
        </FormControl>
      </View>
    );
  })
);

export default Field;
